/**
 * ErrorHandler - Centralized error handling with categorization and logging
 * Based on BurrowTV-LITE-1local template (line ~9783)
 * 
 * Singleton pattern for consistent error handling across the app
 */

class ErrorHandlerClass {
  constructor() {
    if (ErrorHandlerClass.instance) {
      return ErrorHandlerClass.instance;
    }
    
    this.errorLog = [];
    this.maxLogSize = 100; // Cap at 100 errors to prevent memory issues
    this.toast = null; // Will be set by React component
    ErrorHandlerClass.instance = this;
  }
  
  /**
   * Set toast function from Chakra UI
   * Call this from App.js: ErrorHandler.setToast(toast)
   */
  setToast(toastFunction) {
    this.toast = toastFunction;
  }
  
  /**
   * Main error handling method
   * @param {string} context - Where the error occurred (e.g., 'VideoCard.fetchData')
   * @param {Error|string} error - The error object or message
   * @param {string} userMessage - Optional user-friendly message (shown in toast)
   * @param {object} options - Additional options
   */
  handleError(context, error, userMessage = null, options = {}) {
    const {
      silent = false,         // If true, don't show toast
      severity = 'error',     // 'error', 'warning', 'info'
      logToConsole = true     // Log to console
    } = options;
    
    // Extract error info
    const errorInfo = this.parseError(error);
    
    // Create log entry
    const logEntry = {
      timestamp: new Date().toISOString(),
      context,
      message: errorInfo.message,
      stack: errorInfo.stack,
      severity,
      userAgent: navigator.userAgent,
      url: window.location.href
    };
    
    // Add to log (FIFO with size cap)
    this.errorLog.push(logEntry);
    if (this.errorLog.length > this.maxLogSize) {
      this.errorLog.shift();
    }
    
    // Console logging
    if (logToConsole) {
      console.error(`[${context}]`, errorInfo.message);
      if (errorInfo.stack) {
        console.error(errorInfo.stack);
      }
    }
    
    // Show user toast (if not silent)
    if (!silent && this.toast) {
      const title = this.getToastTitle(severity);
      const description = userMessage || this.getUserFriendlyMessage(errorInfo.message, context);
      
      this.toast({
        title,
        description,
        status: severity,
        duration: severity === 'error' ? 5000 : 3000,
        isClosable: true,
        position: 'top-right'
      });
    }
    
    // In production, could send to error tracking service (Sentry, LogRocket, etc.)
    if (process.env.NODE_ENV === 'production' && options.sendToService) {
      this.sendToErrorService(logEntry);
    }
  }
  
  /**
   * Parse error object into consistent format
   */
  parseError(error) {
    if (error instanceof Error) {
      return {
        message: error.message,
        stack: error.stack,
        name: error.name
      };
    }
    
    if (typeof error === 'string') {
      return {
        message: error,
        stack: null,
        name: 'StringError'
      };
    }
    
    if (error && error.response) {
      // Axios error
      return {
        message: error.response.data?.message || error.message,
        stack: error.stack,
        name: 'HTTPError',
        status: error.response.status
      };
    }
    
    return {
      message: 'Unknown error',
      stack: null,
      name: 'UnknownError'
    };
  }
  
  /**
   * Get user-friendly message from technical error
   */
  getUserFriendlyMessage(technicalMessage, context) {
    // Network errors
    if (technicalMessage.includes('Network Error') || technicalMessage.includes('Failed to fetch')) {
      return 'Network connection issue. Please check your internet and try again.';
    }
    
    // Auth errors
    if (technicalMessage.includes('401') || technicalMessage.includes('Unauthorized')) {
      return 'Your session has expired. Please log in again.';
    }
    
    if (technicalMessage.includes('403') || technicalMessage.includes('Forbidden')) {
      return 'You don\'t have permission to access this.';
    }
    
    // Server errors
    if (technicalMessage.includes('500') || technicalMessage.includes('Server Error')) {
      return 'Server is having issues. Please try again in a moment.';
    }
    
    // Timeout errors
    if (technicalMessage.includes('timeout')) {
      return 'Request took too long. Please try again.';
    }
    
    // Video-specific
    if (context.includes('Video') || context.includes('video')) {
      return 'Unable to load video. Please try again.';
    }
    
    // Generic fallback
    return 'Something went wrong. Please try again.';
  }
  
  /**
   * Get toast title based on severity
   */
  getToastTitle(severity) {
    switch (severity) {
      case 'error':
        return 'Error';
      case 'warning':
        return 'Warning';
      case 'info':
        return 'Notice';
      default:
        return 'Error';
    }
  }
  
  /**
   * Send error to external service (placeholder)
   */
  sendToErrorService(logEntry) {
    // TODO: Integrate with Sentry, LogRocket, or custom error tracking
    // For now, just a placeholder
    console.log('[ErrorService] Would send:', logEntry);
  }
  
  /**
   * Get recent errors (for debugging)
   */
  getErrorLog() {
    return [...this.errorLog];
  }
  
  /**
   * Clear error log
   */
  clearLog() {
    this.errorLog = [];
  }
  
  /**
   * Get error statistics
   */
  getStats() {
    const stats = {
      total: this.errorLog.length,
      bySeverity: {},
      byContext: {}
    };
    
    this.errorLog.forEach(entry => {
      // Count by severity
      stats.bySeverity[entry.severity] = (stats.bySeverity[entry.severity] || 0) + 1;
      
      // Count by context
      stats.byContext[entry.context] = (stats.byContext[entry.context] || 0) + 1;
    });
    
    return stats;
  }
}

// Create singleton instance
const ErrorHandler = new ErrorHandlerClass();

// Convenience methods for common scenarios
export const handleAPIError = (context, error, userMessage) => {
  ErrorHandler.handleError(context, error, userMessage, { severity: 'error' });
};

export const handleWarning = (context, message, userMessage) => {
  ErrorHandler.handleError(context, message, userMessage, { severity: 'warning' });
};

export const handleSilentError = (context, error) => {
  ErrorHandler.handleError(context, error, null, { silent: true });
};

export default ErrorHandler;
